import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Star, Trash2, User, Plus, MessageCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { customerAPI } from '../../services/api';

const FavoriteWorkersTab = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newNote, setNewNote] = useState('');

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      const res = await customerAPI.getFavorites();
      setFavorites(res.data || []);
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFavorite = async (workerId) => {
    if (!window.confirm('Remove this worker from favorites?')) return;
    try {
      await customerAPI.removeFavorite(workerId);
      setFavorites(favorites.filter(f => f.worker_id !== workerId));
      toast.success('Removed from favorites');
    } catch (error) {
      toast.error('Failed to remove favorite');
    }
  };

  const getServiceTypeBadge = (type) => {
    const colors = {
      cleaning: 'bg-teal-100 text-teal-700',
      private_concierge: 'bg-pink-100 text-pink-700',
      social_engagement: 'bg-[#F4C2C2]/15 text-purple-700'
    };
    const labels = {
      cleaning: 'Cleaning',
      private_concierge: 'Private Concierge',
      social_engagement: 'Social Engagement'
    };
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full ${colors[type] || 'bg-[var(--background-subtle)] text-[var(--text-primary)]'}`}>
        {labels[type] || type}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="card p-12 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-[var(--text-muted)]">Loading favorites...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
            <Heart className="w-6 h-6 text-[var(--primary)]" />
            Favorite Workers
          </h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">
            Save your preferred workers for easy rebooking
          </p>
        </div>
      </div>

      {favorites.length === 0 ? (
        <div className="card p-12 text-center">
          <Heart className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
          <h3 className="text-lg font-bold mb-2">No Favorite Workers Yet</h3>
          <p className="text-[var(--text-secondary)] mb-4">
            After a great service, add your worker to favorites to easily request them again!
          </p>
          <p className="text-sm text-[var(--text-muted)]">
            You can add workers to favorites from your completed booking details.
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <AnimatePresence>
            {favorites.map((fav) => (
              <motion.div
                key={fav.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="card p-6"
                data-testid={`favorite-worker-${fav.worker_id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full bg-[var(--accent)] flex items-center justify-center flex-shrink-0">
                    {fav.worker_photo ? (
                      <img 
                        src={fav.worker_photo} 
                        alt={fav.worker_name}
                        className="w-14 h-14 rounded-full object-cover"
                      />
                    ) : (
                      <User className="w-7 h-7 text-[var(--primary)]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold truncate">{fav.worker_name}</h3>
                    <div className="flex items-center gap-1 text-sm text-yellow-500 mb-2">
                      <Star className="w-4 h-4 fill-current" />
                      <span>{fav.worker_rating?.toFixed(1) || '5.0'}</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {fav.worker_service_types?.map(type => (
                        <span key={type}>{getServiceTypeBadge(type)}</span>
                      ))}
                    </div>
                    {fav.notes && (
                      <p className="text-xs text-[var(--text-muted)] italic">"{fav.notes}"</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 mt-4 pt-4 border-t border-[var(--border-primary)]">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleRemoveFavorite(fav.worker_id)}
                    data-testid={`remove-favorite-${fav.worker_id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Remove
                  </Button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default FavoriteWorkersTab;
